import React, { useState, useEffect, useRef } from "react";
import { useTryOn } from "../contexts/TryOnContext";
import { PRODUCTS } from "../data/products";
import "../styles/hologram.css";
import "./Step1Attract.css";

/**
 * FloatingProduct - Component that crossfades between multiple product images
 */
function FloatingProduct({ product, className, index }) {
  // Two persistent image layers — we swap which one is "active" (opacity 1)
  // instead of mounting/unmounting, which eliminates flicker from decode delay.
  const [layerA, setLayerA] = useState(product.imagePaths[0]);
  const [layerB, setLayerB] = useState(product.imagePaths[0]);
  const [activeLayer, setActiveLayer] = useState('A');
  const currentIdxRef = useRef(0);

  // Preload every image for this product so crossfades are always warm
  useEffect(() => {
    product.imagePaths.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [product.imagePaths]);

  useEffect(() => {
    if (product.imagePaths.length <= 1) return;

    let timer;
    const cycle = () => {
      const delay = 6000 + Math.random() * 4000;
      timer = setTimeout(() => {
        const nextIdx = (currentIdxRef.current + 1) % product.imagePaths.length;
        const nextSrc = product.imagePaths[nextIdx];

        // Load the new image into the INACTIVE layer, then flip active
        setActiveLayer((prev) => {
          if (prev === 'A') {
            setLayerB(nextSrc);
            return 'B';
          } else {
            setLayerA(nextSrc);
            return 'A';
          }
        });
        currentIdxRef.current = nextIdx;
        cycle();
      }, delay);
    };

    const initTimer = setTimeout(cycle, index * 3000 + Math.random() * 1500);
    return () => {
      clearTimeout(timer);
      clearTimeout(initTimer);
    };
  }, [product.imagePaths, index]);

  return (
    <div className={className}>
      <div className="card-face">
        <div className="photo-area">
          <img
            src={layerA}
            alt={product.name}
            className={`product-photo product-photo-layer ${activeLayer === 'A' ? 'is-active' : ''}`}
          />
          <img
            src={layerB}
            alt={product.name}
            className={`product-photo product-photo-layer ${activeLayer === 'B' ? 'is-active' : ''}`}
          />
        </div>

        <div className="card-info">
          <div className="product-name">{product.name}</div>
          <div className="product-price">{product.price}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Step 1: Holographic attract screen with floating product cards
 */
function Step1Attract() {
  const { setCurrentStep } = useTryOn();

  const handleTakePhoto = (e) => {
    e.stopPropagation();
    console.log("User chose: Take a Photo");
    setCurrentStep(2);
  };

  return (
    <div className="step1-hologram-container">
      <div className="rails-overlay" />
      <div className="floor-ring" />
      <div className="glass-overlay-step1" />{" "}
      {/* BIG curved glass highlight + vignette */}
      <div className="vignette-overlay" />
      <div className="grain-overlay" />
      {/* Floating product cards */}
      <div className="floating-products-holo">
        {PRODUCTS.map((product, index) => (
          <FloatingProduct
            key={product.id}
            product={product}
            className={`floating-product-card extruded card-${index + 1}`}
            index={index}
          />
        ))}
      </div>
      <div className="cta-hologram">
        <img className="cta-logo" src="/skechers.png" alt="Skechers" />

        <h1 className="cta-title-holo holo-text">STEP INTO THE FUTURE</h1>
        <p className="cta-subtitle-holo">Experience Holographic Fashion</p>

        <div className="choice-buttons">
          <button
            className="choice-btn take-photo-btn holo-card"
            onClick={handleTakePhoto}
          >
            <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="4"/>
            </svg>
            <span className="btn-text">Take a Photo</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Step1Attract;
